import React, { useState } from "react";
import { Link, usePathname } from "@/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { ATHLETE_ROLE, VENUE_OWNER_ROLE, STAFF_ROLE } from "@/constants/roles";
import { useTranslations } from "next-intl";
import { useScrollDirection } from "@/hooks/useScrollDirection";
import {
  Home,
  Building2,
  Calendar,
  CalendarClock,
  Users,
  MoreHorizontal,
  BarChart2,
} from "lucide-react";

interface TabItem {
  key: string;
  label: string;
  href: string;
  icon: React.ElementType;
  show?: boolean;
  // Sections own more than one route, so equality against href is not enough.
  match?: (pathname: string) => boolean;
}

interface BottomTabNavigationProps {
  // Picking the venue belongs to the nav bar's switcher, at every width. These
  // tabs only have to stay inside whatever it picked.
  activeVenueId: string | null;
}

const BottomTabNavigation: React.FC<BottomTabNavigationProps> = ({
  activeVenueId,
}) => {
  const t = useTranslations("Navigation");
  const { user } = useAuth();
  const pathname = usePathname();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const { scrollDirection, isNearBottom } = useScrollDirection();

  const getTabItems = (): TabItem[] => {
    const isAthlete = user?.roles?.includes(ATHLETE_ROLE);
    const isVenueOwner = user?.roles?.includes(VENUE_OWNER_ROLE);
    const isStaff = user?.roles?.includes(STAFF_ROLE);

    // Don't show tab items if profile is not finalized
    if (!user?.irf) {
      return [];
    }

    if (isAthlete) {
      return [
        {
          key: "home",
          label: t("Menu.Home"),
          href: "/home",
          icon: Home,
        },
        {
          key: "browse",
          label: t("Menu.BrowseVenues"),
          href: "/browse",
          icon: Building2,
        },
      ];
    }

    if (isStaff) {
      return [
        {
          key: "reservations",
          label: t("Menu.Reservations"),
          href: activeVenueId
            ? `/venues/${activeVenueId}/reservations`
            : "/venues",
          icon: Calendar,
          show: true,
          match: (p) => p.includes("/reservations"),
        },
        {
          key: "recurring_reservations",
          label: t("Menu.RecurringReservations"),
          // Carries the venue so the tabs do not silently reset the scope.
          href: activeVenueId
            ? `/recurring-reservations?venueId=${activeVenueId}`
            : "/recurring-reservations",
          icon: CalendarClock,
          show: true,
          match: (p) => p === "/recurring-reservations",
        },
      ];
    }

    if (isVenueOwner) {
      // Main tabs for venue owners: Reservations, Recurring Reservations, Venues, More
      return [
        {
          key: "reservations",
          label: t("Menu.Reservations"),
          href: activeVenueId
            ? `/venues/${activeVenueId}/reservations`
            : "/venues",
          icon: Calendar,
          show: true,
          match: (p) => p.includes("/reservations"),
        },
        {
          key: "recurring_reservations",
          label: t("Menu.RecurringReservations"),
          // Carries the venue so the tabs do not silently reset the scope.
          href: activeVenueId
            ? `/recurring-reservations?venueId=${activeVenueId}`
            : "/recurring-reservations",
          icon: CalendarClock,
          show: true,
          match: (p) => p === "/recurring-reservations",
        },
        {
          key: "venues",
          label: t("Menu.Venues"),
          // The venue switcher already picks which venue you are in, so this
          // goes straight to that venue's overview instead of via the list.
          href: activeVenueId ? `/venues/${activeVenueId}/overview` : "/venues",
          icon: Building2,
          // The list is still reachable (no active venue, or by URL), so the
          // tab stays lit there as well as on the overview it leads to.
          match: (p) => p === "/venues" || p.includes("/overview"),
        },
        {
          key: "more",
          label: t("Menu.More"),
          href: "#",
          icon: MoreHorizontal,
        },
      ];
    }

    return [];
  };

  const tabItems = getTabItems();

  const getMoreMenuItems = (): TabItem[] => {
    const isVenueOwner = user?.roles?.includes(VENUE_OWNER_ROLE);

    if (isVenueOwner) {
      return [
        {
          key: "staff",
          label: t("Menu.Staff"),
          href: activeVenueId ? `/venues/${activeVenueId}/staff` : "/venues",
          icon: Users,
          show: !!activeVenueId,
          match: (p) => p.includes("/staff"),
        },
        {
          key: "reports",
          label: t("Menu.Reports"),
          href: activeVenueId ? `/reports?venueId=${activeVenueId}` : "/reports",
          icon: BarChart2,
          show: true,
          match: (p) => p === "/reports",
        },
      ];
    }

    return [];
  };

  const moreMenuItems = getMoreMenuItems();

  const isItemActive = (item: TabItem): boolean =>
    item.match ? item.match(pathname) : pathname === item.href;

  const isTabActive = (tab: TabItem): boolean =>
    tab.key === "more"
      ? // The More tab stands in for whatever it holds.
        moreMenuItems.some(isItemActive)
      : isItemActive(tab);

  // Determine if navigation should be hidden
  const shouldHideNavigation = scrollDirection === 'down' && !isMoreMenuOpen || isNearBottom;

  return (
    <motion.nav
      initial={{ y: 100 }}
      animate={{ 
        y: shouldHideNavigation ? 100 : 0,
        transition: { type: "spring", stiffness: 300, damping: 30 }
      }}
      className="fixed bottom-0 left-0 right-0 z-40 bg-gray-900/95 backdrop-blur-md border-t border-gray-700 md:hidden"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}
    >
      <div className="flex items-center justify-around px-2 py-2">
        {tabItems.map((tab) => {
          if (tab.show === false) return null;
          
          const Icon = tab.icon;
          const isActive = isTabActive(tab);

          return (
            <motion.div
              key={tab.key}
              className="flex-1 flex justify-center"
              whileTap={{ scale: 0.95 }}
            >
              {tab.key === "more" ? (
                <button
                  onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                  className={`flex flex-col items-center justify-center px-3 py-2 rounded-xl transition-all duration-300 min-h-[60px] relative ${
                    isActive
                      ? "text-white bg-gray-700/30"
                      : "text-gray-400 hover:text-gray-300 hover:bg-gray-800/30"
                  }`}
                  aria-label={tab.label}
                  aria-expanded={isMoreMenuOpen}
                >
                  <Icon className="w-5 h-5 mb-1" />
                  <span className="text-xs font-medium leading-none">
                    {tab.label.length > 8 ? tab.label.substring(0, 8) + "..." : tab.label}
                  </span>
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-gray-700/20 rounded-xl"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 35 }}
                    />
                  )}
                </button>
              ) : (
                <Link
                  href={tab.href as any}
                  className={`flex flex-col items-center justify-center px-3 py-2 rounded-xl transition-all duration-300 min-h-[60px] relative ${
                    isActive
                      ? "text-white bg-gray-700/30"
                      : "text-gray-400 hover:text-gray-300 hover:bg-gray-800/30"
                  }`}
                  aria-label={tab.label}
                >
                  <Icon className="w-5 h-5 mb-1" />
                  <span className="text-xs font-medium leading-none">
                    {tab.label.length > 8 ? tab.label.substring(0, 8) + "..." : tab.label}
                  </span>
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-gray-700/20 rounded-xl"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 35 }}
                    />
                  )}
                </Link>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* More Menu Overlay */}
      <AnimatePresence>
        {isMoreMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-full left-0 right-0 bg-gray-800/95 backdrop-blur-md border-t border-gray-700 max-h-48 overflow-y-auto"
          >
            <div className="px-4 py-2">
              <p className="text-xs text-gray-400 font-medium mb-2 uppercase tracking-wide">
                {t("Menu.More")}
              </p>
              {moreMenuItems.map((item) => {
                if (item.show === false) return null;
                const Icon = item.icon;

                return (
                  <Link
                    key={item.key}
                    href={item.href as any}
                    className={`flex items-center space-x-2 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700/50 hover:text-white transition-colors rounded-lg mb-1 ${
                      isItemActive(item) ? "bg-gray-700/30 text-white" : ""
                    }`}
                    onClick={() => setIsMoreMenuOpen(false)}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default BottomTabNavigation;